/**
 * Worker types (WagePro) — pure UI helpers, no React.
 *
 * Mirrors the API's `services/payroll/worker_type.ts`: the API is the
 * authority (it excludes contractors from pay runs and refuses every
 * employee-only action for them); these helpers only decide what the UI
 * offers, so the screen never shows a control the server would refuse.
 */

import type { EmploymentStatus, EmploymentType } from '../data/types';

/** Display order for pickers, filters and the explainer banner. */
export const WORKER_TYPE_ORDER: readonly EmploymentType[] = [
  'full_time',
  'part_time',
  'contract',
  'contractor',
];

/** 100% FTE, in basis points. */
export const FULL_FTE_BPS = 10_000;

/** What a worker type takes part in. */
export interface WorkerTypeCapabilities {
  /** Included in pay runs (payslips, disbursement files). */
  on_payroll: boolean;
  /** Statutory contributions / tax withheld (EPF/ETF/APIT, BPJS/PPh 21, …). */
  statutory: boolean;
  /** Leave balances, requests and policy groups. */
  leave: boolean;
  /** Employee self-service invite / access. */
  ess: boolean;
}

const EMPLOYEE_CAPABILITIES: WorkerTypeCapabilities = {
  on_payroll: true,
  statutory: true,
  leave: true,
  ess: true,
};

const CONTRACTOR_CAPABILITIES: WorkerTypeCapabilities = {
  on_payroll: false,
  statutory: false,
  leave: false,
  ess: false,
};

/**
 * Full-time, part-time and fixed-term workers are employees with the full
 * statutory treatment; an independent contractor takes part in none of it
 * (paid through Ledger bills instead).
 */
export function worker_type_capabilities(
  type: EmploymentType,
): WorkerTypeCapabilities {
  return type === 'contractor'
    ? CONTRACTOR_CAPABILITIES
    : EMPLOYEE_CAPABILITIES;
}

/** Whether the worker is an independent contractor (not an employee). */
export function is_contractor(type: EmploymentType): boolean {
  return type === 'contractor';
}

/**
 * How many ACTIVE (non-terminated) workers are contractors -- drives the
 * run-payroll "contractors are not included" note, which must not count a
 * contractor whose engagement has already ended.
 */
export function count_active_contractors(
  workers: readonly {
    employment_type: EmploymentType;
    status: EmploymentStatus;
  }[],
): number {
  return workers.filter(
    (w) => is_contractor(w.employment_type) && w.status !== 'terminated',
  ).length;
}

/**
 * Whether the employee form should remind the user to update the employment
 * start date: the worker was saved as a contractor and is being switched to
 * an EMPLOYEE type. Leave accrual runs from `employment_start_date`, so a
 * start date that marks the beginning of the contractor engagement would
 * credit the contractor months as employee service (accepted behaviour,
 * ADR 20260929_01 §6).
 */
export function needs_start_date_hint(
  original: EmploymentType | null,
  next: EmploymentType,
): boolean {
  return original === 'contractor' && next !== 'contractor';
}

/**
 * Whole-percent FTE (1–100) → basis points. Integer arithmetic only.
 * Returns `null` for anything outside 1–100 or non-integer.
 */
export function fte_percent_to_bps(percent: number): number | null {
  if (!Number.isInteger(percent) || percent < 1 || percent > 100) return null;
  return percent * 100;
}

/**
 * Basis points → whole percent for display and for the form's FTE field,
 * rounded half-up (5050 bps → 51%) and clamped to the 1–100 range the form
 * accepts, so a positive FTE below 0.5% (1–49 bps, only writable through the
 * API) reads as 1% rather than a misleading "0%" the form would then refuse.
 * `null` in → `null` out.
 */
export function fte_bps_to_percent(bps: number | null): number | null {
  if (bps === null) return null;
  const rounded = Math.floor((bps + 50) / 100);
  return Math.min(100, Math.max(1, rounded));
}
